import type { ReactNode } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { InleagueLastRead, navLabel } from '@region13/ui'
import { COACH_NAV, useNavTitle } from '../lib/nav'
import { useQuery } from '@tanstack/react-query'
import { type CoachPracticeSlot, type CoachPracticeView } from '@region13/shared-types'
import { api, collectsGameCards, type CoachTeamWithGames } from '../lib/api'
import { Card } from '../components/ui/Card'
import { TeamScope } from '../components/TeamScope'
import { TeamNameHeading } from '../components/TeamNameHeading'
import { VolunteerList } from '../components/VolunteerDetails'
import { AwayGamesCard } from '../components/AwayGamesCard'
import { CardSection } from '../components/ui/CardSection'
import { nextGame, gameLine, opponentOf } from '../lib/nextGame'
import { shortDate } from '../lib/practiceDate'
import { practiceMovingLine, practiceStartsLine, SECOND_PRACTICE_PENDING } from '../lib/practiceCopy'
import { SCHEDULE_NOT_PUBLISHED_MESSAGE } from '../lib/scheduleCopy'

export const Route = createFileRoute('/')({ component: Home })

/** A labeled row in the volunteers block. */
function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex justify-between gap-3 text-sm">
      <span className="shrink-0 text-ink-soft">{label}</span>
      <span className="min-w-0 text-right text-ink">{children}</span>
    </div>
  )
}

/** One slot's day/time, place, and map link — the same fact set whether this
 *  is a standard team's only practice or one of an Upper Division team's two. */
function SlotBlock({ slot, spaced }: { slot: CoachPracticeSlot; spaced: boolean }) {
  return (
    <span className={spaced ? 'mt-2 block' : 'block'}>
      <span className="block">{slot.when}</span>
      <span className="block">{slot.where}</span>
      <a
        className="text-xs font-semibold text-brand-red-dark underline"
        href={slot.mapUrl}
        target="_blank"
        rel="noopener"
      >
        View field map
      </a>
    </span>
  )
}

/** The Practice row's value for each of CoachPracticeView's six states. Home
 *  is a summary: `assigned`/`partially_placed` get the day/time, venue, and a
 *  map link for every slot the team currently holds; `upcoming_only` gets the
 *  start date and venue; `unplaced` and `not_scheduled` each get a short,
 *  distinct line (never worded so one reads like the other — see
 *  PracticeSlotCard, which carries the fuller copy); and `excluded` says the row
 *  does not apply to this team at all, which is the one line here that must not
 *  read as "coming soon".
 *
 *  `current` and `upcoming` are plural because an Upper Division team holds
 *  two concurrent practices. This branch used to read a single `current`
 *  object, so an array (always truthy, even empty) would have silently shown
 *  every field as `undefined` instead of the intended data-error fallback --
 *  the length checks below are what keep that from happening.
 *
 *  Exported (not module-private) so tests can render it directly with a
 *  `CoachPracticeView` prop, the same way `routes/practice.test.tsx` already
 *  imports `PracticePrefsForm`/`PairingCard` from its neighbouring route
 *  module — this component takes no query of its own, so no QueryClient
 *  wrapper is needed either. */
export function PracticeValue({ view }: { view: CoachPracticeView }) {
  // 'excluded' first, and worded as "does not apply" rather than "pending":
  // 4U/5U never get a practice field, so every other line here is a promise.
  if (view.status === 'excluded') return <>No separate practice field (preschool groups meet at a set time)</>
  if (view.status === 'not_scheduled') return <>Practice fields not set for the season yet</>
  if (view.status === 'unplaced') return <>Other teams have slots; yours is still pending</>
  if (view.status === 'ended' && view.through) return <>Practice ended {shortDate(view.through)}</>
  if (view.status === 'upcoming_only' && view.upcoming.length > 0) {
    return (
      <>
        {view.upcoming.map((slot, i) => (
          <span key={`${slot.when}-${slot.where}`} className={i > 0 ? 'mt-2 block' : undefined}>
            {practiceStartsLine(slot)}
          </span>
        ))}
      </>
    )
  }
  if ((view.status === 'assigned' || view.status === 'partially_placed') && view.current.length > 0) {
    return (
      <>
        {view.current.map((slot, i) => (
          <SlotBlock key={`${slot.when}-${slot.where}`} slot={slot} spaced={i > 0} />
        ))}
        {/* A UD coach missing their second practice needs to know it HERE too:
            Home is the page a coach actually lands on, and fixing only
            PracticeSlotCard would leave this row claiming they are fully
            placed. Matches the sentence shown on the fuller card. */}
        {view.status === 'partially_placed' ? <span className="mt-1 block">{SECOND_PRACTICE_PENDING}</span> : null}
        {/* A pending re-home belongs HERE, not only on the /practice card:
            this branch used to read `current` and drop `upcoming` entirely,
            so a move was invisible unless the coach navigated away. Placed
            after the map link(s) because those point at the CURRENT venue,
            and the moving line names the new one explicitly. */}
        {view.upcoming.map((slot) => (
          <span key={`${slot.when}-${slot.where}`} className="mt-1 block">
            {practiceMovingLine(slot)}
          </span>
        ))}
      </>
    )
  }
  // Defensive fallback for a shape the API contract rules out (an
  // upcoming_only/assigned/partially_placed status whose matching slot list
  // is empty).
  return <>Couldn't load your practice slot.</>
}

/** This team's published practice slot, as a compact row - shares the
 *  ['practice-slot', ext] query key with PracticeSlotCard (the /practice
 *  route's fuller card) so the two views never double-fetch the same team. */
function PracticeRow({ teamExternalId }: { teamExternalId: string }) {
  const q = useQuery({
    queryKey: ['practice-slot', teamExternalId],
    queryFn: () => api.practiceSlot(teamExternalId),
  })

  return (
    <Row label="Practice">
      {q.isLoading ? 'Loading…' : q.isError || !q.data ? "Couldn't load your practice slot." : <PracticeValue view={q.data} />}
    </Row>
  )
}

/** One team's Home panel: next game, assigned InLeague volunteers, this
 *  team's published practice slot, away fixtures (if any), and quick links
 *  into that team's Cards / Referees / Jerseys.
 *
 *  Exported (not module-private) so tests can render it directly, the same
 *  way `referees.tsx` exports `TeamCrew` for its own empty-state tests. */
export function TeamSummary({ team, adminSelected }: { team: CoachTeamWithGames; adminSelected: boolean }) {
  // Coach teams carry games inline; a superadmin-selected team has games: [] and
  // loads them on demand.
  const gamesQ = useQuery({
    queryKey: ['team-games', team.externalId],
    queryFn: () => api.teamGames(team.externalId),
    enabled: adminSelected,
  })
  const games = adminSelected ? (gamesQ.data?.games ?? []) : team.games
  const upcoming = nextGame(games)

  const volQ = useQuery({ queryKey: ['volunteers', team.externalId], queryFn: () => api.volunteers(team.externalId) })
  const v = volQ.data

  return (
    <Card as="section">
      <div>
        <TeamNameHeading team={team} />
        <p className="text-sm text-ink-soft">{team.division}</p>
      </div>

      {/* 'No upcoming games' is accurate for a team that already has (past)
          games and simply none scheduled next. But when this team has NO
          games at all, that same sentence reads as a permanent claim about a
          season that's simply not published yet (spec D2) — the same
          disambiguation `referees.tsx`'s EmptyCrewMessage already makes. */}
      <Row label="Next game">
        {adminSelected && gamesQ.isLoading
          ? 'Loading…'
          : adminSelected && gamesQ.isError
            ? "Couldn't load games"
            : upcoming
              ? gameLine(upcoming, opponentOf(upcoming, team.externalId))
              : games.length === 0
                ? SCHEDULE_NOT_PUBLISHED_MESSAGE
                : 'No upcoming games'}
      </Row>

      <CardSection title="Assigned volunteers">
        {volQ.isLoading ? (
          <p role="status" className="text-sm text-ink-soft">Loading…</p>
        ) : volQ.isError ? (
          <p role="alert" className="text-sm text-brand-red-dark">Couldn't load volunteers.</p>
        ) : (
          <div className="flex flex-col gap-1">
            <Row label="Coach"><VolunteerList people={v?.coach ? [v.coach] : []} empty="—" /></Row>
            <Row label="Assistant"><VolunteerList people={v?.assistant ? [v.assistant] : []} empty="—" /></Row>
            <Row label="Referees"><VolunteerList people={v?.referees ?? []} empty="None assigned" /></Row>
            <Row label="Managers"><VolunteerList people={v?.managers ?? []} empty="None assigned" /></Row>
          </div>
        )}
      </CardSection>

      <PracticeRow teamExternalId={team.externalId} />

      <AwayGamesCard teamExternalId={team.externalId} />

      <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-line pt-3 text-sm font-semibold text-brand-red-dark">
        {/* Hidden for a division that does not use cards, so Home does not send
            a coach to a page whose only content is an explanation. The page
            still explains itself if they arrive by the nav. */}
        {collectsGameCards(team) && (
          <Link to="/cards" className="underline hover:text-brand-maroon">{navLabel(COACH_NAV, '/cards')}</Link>
        )}
        <Link to="/referees" className="underline hover:text-brand-maroon">{navLabel(COACH_NAV, '/referees')}</Link>
        <Link to="/jerseys" search={{ team: team.externalId }} className="underline hover:text-brand-maroon">
          {navLabel(COACH_NAV, '/jerseys')}
        </Link>
        <Link to="/forms" className="underline hover:text-brand-maroon">{navLabel(COACH_NAV, '/forms')}</Link>
        <Link to="/incidents" className="underline hover:text-brand-maroon">{navLabel(COACH_NAV, '/incidents')}</Link>
      </div>
    </Card>
  )
}

function Home() {
  return (
    <TeamScope
      heading={useNavTitle()}
      emptyText="No teams found for your account this season."
      hint="Pick a team to see its next game and volunteers."
      subheading={<InleagueLastRead step="teams" className="mt-1" />}
    >
      {(team, adminSelected) => <TeamSummary team={team} adminSelected={adminSelected} />}
    </TeamScope>
  )
}
